import type { Column } from '~shared/rundown/column'
import { CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'
import type { IndexedCue } from '~shared/utils'

import { makeCellKey } from '../cell/cell.schema'

import type { GridSelection } from './grid.store'

export type GridNavigationDirection = 'left' | 'right' | 'up' | 'down'

/** The part of an {@link IndexedCue} navigation needs. */
type NavigableCue = Pick<IndexedCue, 'id' | 'type'>

export type GridNavigationModel = {
  cues: readonly NavigableCue[]
  columns: readonly Column[]
}

export function getNextGridSelection(
  selection: GridSelection,
  direction: GridNavigationDirection,
  model: GridNavigationModel,
): GridSelection {
  if (!selection) return null

  const { cues, columns } = model

  const cueIndex = cues.findIndex((cue) => cue.id === selection.cueId)

  if (cueIndex === -1) return selection

  if (selection.type === 'cue') {
    return moveFromCue(selection, direction, cueIndex, model)
  }

  const columnIndex = columns.findIndex(
    (column) => column.id === selection.columnId,
  )

  if (columnIndex === -1) return selection

  switch (direction) {
    case 'left': {
      if (columnIndex === 0) {
        return {
          type: 'cue',
          cueId: selection.cueId,
        }
      }

      const previousColumn = columns[columnIndex - 1]

      if (!previousColumn) return selection

      return makeCellSelection(selection.cueId, previousColumn.id)
    }

    case 'right': {
      const nextColumn = columns[columnIndex + 1]

      if (!nextColumn) return selection

      return makeCellSelection(selection.cueId, nextColumn.id)
    }

    case 'up':
      return moveCellVertically(selection, cueIndex, -1, model)

    case 'down':
      return moveCellVertically(selection, cueIndex, 1, model)
  }
}

function makeCellSelection(
  cueId: Cue['id'],
  columnId: Column['id'],
): Exclude<GridSelection, null | { type: 'cue' }> {
  return {
    type: 'cell',
    cueId,
    columnId,
    cellKey: makeCellKey(cueId, columnId),
  }
}

function moveCellVertically(
  selection: Extract<GridSelection, { type: 'cell' }>,
  currentCueIndex: number,
  offset: -1 | 1,
  model: GridNavigationModel,
): GridSelection {
  const { cues } = model

  // Skip headings/groups because they have no cells.
  for (
    let index = currentCueIndex + offset;
    index >= 0 && index < cues.length;
    index += offset
  ) {
    const cue = cues[index]

    if (cue && cueHasCells(cue)) {
      return makeCellSelection(cue.id, selection.columnId)
    }
  }

  return selection
}

// If headings/groups should never be selectable, change cue movement to skip them in the same way as cell vertical movement.
function moveFromCue(
  selection: Extract<GridSelection, { type: 'cue' }>,
  direction: GridNavigationDirection,
  cueIndex: number,
  model: GridNavigationModel,
): GridSelection {
  const { cues, columns } = model

  switch (direction) {
    case 'left':
      return selection

    case 'right': {
      const cue = cues[cueIndex]
      const firstColumn = columns[0]

      if (!cue || !cueHasCells(cue) || !firstColumn) {
        return selection
      }

      return makeCellSelection(cue.id, firstColumn.id)
    }

    case 'up': {
      const previousCue = cues[cueIndex - 1]

      return previousCue ? { type: 'cue', cueId: previousCue.id } : selection
    }

    case 'down': {
      const nextCue = cues[cueIndex + 1]

      return nextCue ? { type: 'cue', cueId: nextCue.id } : selection
    }
  }
}

/**
 * Determines if cue has cells.
 *
 * Only 'CUE' type does, 'HEADING' and 'GROUP' do not.
 */
function cueHasCells(cue: NavigableCue): boolean {
  return cue.type === CueType.CUE
}
